/**
 * AnalysisVisuals — Visual charts for stock analysis
 *
 * Contains 3 chart types:
 * - ScenarioBar: horizontal bars showing bull/real/bear scenarios
 * - ValuationGauge: how cheap/expensive the stock is vs historical
 * - SectorDonut: revenue breakdown by segment (when available)
 */

/**
 * ScenarioBar: visual representation of bull/real/bear multipliers
 */
export function ScenarioBar({ scenarios, amount = 1000 }) {
  if (!scenarios) return null;

  const bullValue = amount * scenarios.bull.multiplier;
  const realValue = amount * scenarios.real.multiplier;
  const bearValue = amount * scenarios.bear.multiplier;
  const maxValue = Math.max(bullValue, realValue, bearValue, amount * 1.1);

  const Bar = ({ label, value, color, emoji }) => {
    const width = (value / maxValue) * 100;
    const returnPct = (((value / amount) - 1) * 100).toFixed(0);
    const isPositive = value >= amount;
    return (
      <div className="mb-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-[#181D27]">{emoji} {label}</span>
          <span className="text-xs font-bold" style={{ color }}>
            ${Math.round(value).toLocaleString()} ({isPositive ? '+' : ''}{returnPct}%)
          </span>
        </div>
        <div className="h-3 bg-[#F3F4F6] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{ width: `${width}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="my-3">
      <Bar emoji="🚀" label="Optimista" value={bullValue} color="#059669" />
      <Bar emoji="⚖️" label="Realista" value={realValue} color="#2A59C6" />
      <Bar emoji="⚠️" label="Pesimista" value={bearValue} color="#DC2626" />
      <div className="text-[10px] text-[#9CA3AF] text-center mt-2">
        Proyección de ${amount.toLocaleString()} para {scenarios.horizon}
      </div>
    </div>
  );
}

/**
 * ValuationGauge: shows if current P/E is cheap, fair, or expensive vs historical
 */
export function ValuationGauge({ currentPE, historicalAvgPE, label = 'P/E' }) {
  if (!currentPE || !historicalAvgPE) return null;

  // Range: 50% below historical to 50% above
  const minPE = historicalAvgPE * 0.5;
  const maxPE = historicalAvgPE * 1.5;
  const clampedPE = Math.max(minPE, Math.min(maxPE, currentPE));
  const position = ((clampedPE - minPE) / (maxPE - minPE)) * 100;

  // Color based on position vs historical
  let status, color, bg;
  if (currentPE < historicalAvgPE * 0.85) {
    status = 'Barata';
    color = '#059669';
    bg = '#ECFDF5';
  } else if (currentPE < historicalAvgPE * 1.15) {
    status = 'Justa';
    color = '#2A59C6';
    bg = '#F0F4FF';
  } else {
    status = 'Cara';
    color = '#DC2626';
    bg = '#FEF2F2';
  }

  return (
    <div className="my-3 p-3 rounded-xl border" style={{ backgroundColor: bg, borderColor: color + '30' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#181D27]">Valoración {label}</span>
        <span className="text-xs font-bold uppercase" style={{ color }}>{status}</span>
      </div>
      <div className="relative h-3 bg-white rounded-full overflow-hidden border border-[#E5E7EB]">
        {/* Background gradient */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'linear-gradient(to right, #059669 0%, #2A59C6 50%, #DC2626 100%)',
            opacity: 0.2,
          }}
        />
        {/* Marker */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white"
          style={{ left: `calc(${position}% - 6px)`, backgroundColor: color }}
        />
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-[#6B7280]">
        <span>Barata ({minPE.toFixed(0)}x)</span>
        <span>Promedio {historicalAvgPE.toFixed(0)}x</span>
        <span>Cara ({maxPE.toFixed(0)}x)</span>
      </div>
      <div className="text-[10px] text-[#9CA3AF] text-center mt-1">
        Actual: <strong>{currentPE.toFixed(1)}x</strong> vs histórico {historicalAvgPE}x
      </div>
    </div>
  );
}

/**
 * SectorDonut: shows revenue breakdown by segment
 * data: [{ label, pct, color }]
 */
export function SectorDonut({ data, title = 'Composición' }) {
  if (!data || data.length === 0) return null;

  const size = 140;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.35;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="my-3 p-3 rounded-xl border border-[#E5E7EB] bg-white">
      <div className="text-xs font-semibold text-[#181D27] mb-2">{title}</div>
      <div className="flex items-center gap-4">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
          {data.map((item, i) => {
            const dash = (item.pct / 100) * circumference;
            const gap = circumference - dash;
            const segment = (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={item.color}
                strokeWidth={size * 0.18}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${cx} ${cy})`}
              />
            );
            offset += dash;
            return segment;
          })}
        </svg>
        <div className="flex-1 space-y-1.5">
          {data.map((item, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-[#4B5563]">{item.label}</span>
              </div>
              <span className="font-semibold text-[#181D27]">{item.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
